import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { registerAgentConversationOpener } from '@apps/web/lib/active_agent_conversation';
import { type ChatComposerDraft } from '@apps/web/lib/chat_composer_draft';
import {
    type ChatPanelMode,
    readPersistedChatVisible,
    writePersistedChatVisible,
} from '@apps/web/lib/chat_ui_persistence';

export type ComposerPrefillSource =
    | 'learn_phoebe'
    | 'calendar_selection'
    | 'training_coverage'
    | 'coverage_bulk_ask_availability'
    | 'training_coverage_bulk_ask_availability';

type ComposerPrefillRequest = {
    readonly text: string;
    readonly source: ComposerPrefillSource;
    readonly onDelivered?: () => void;
    readonly newConversation?: boolean;
};

type UIContextType = {
    readonly isChatVisible: boolean;
    readonly toggleChatVisibility: () => void;
    readonly showChat: () => void;
    readonly hideChat: () => void;
    readonly autoOpenChat: () => void;
    readonly closeAutoOpenedChat: () => void;
    readonly openChatWithId: (
        chatId: string,
        options?: {
            readonly mode?: ChatPanelMode;
            readonly organizationId?: string | null;
        },
    ) => void;
    readonly requestedChatId: string | null;
    readonly requestedChatOrganizationId: string | null;
    readonly requestedChatMode: ChatPanelMode | null;
    readonly clearRequestedChatId: () => void;
    readonly clearRequestedChatMode: () => void;
    readonly clearAutoOpenSuppression: () => void;
    readonly openNewChat: () => void;
    readonly newChatRequested: boolean;
    readonly clearNewChatRequest: () => void;
    readonly openChatWithComposerText: (
        text: string,
        source: ComposerPrefillSource,
        onDelivered?: () => void,
        options?: { readonly newConversation?: boolean },
    ) => void;
    readonly requestedComposerPrefill: ComposerPrefillRequest | null;
    readonly clearRequestedComposerPrefill: () => void;
    readonly openChatWithComposerDraft: (draft: ChatComposerDraft) => void;
    readonly requestedComposerDraft: ChatComposerDraft | null;
    readonly clearRequestedComposerDraft: () => void;
};

const UIContext = createContext<UIContextType>({
    isChatVisible: false,
    toggleChatVisibility: () => {},
    showChat: () => {},
    hideChat: () => {},
    autoOpenChat: () => {},
    closeAutoOpenedChat: () => {},
    openChatWithId: () => {},
    requestedChatId: null,
    requestedChatOrganizationId: null,
    requestedChatMode: null,
    clearRequestedChatId: () => {},
    clearRequestedChatMode: () => {},
    clearAutoOpenSuppression: () => {},
    openNewChat: () => {},
    newChatRequested: false,
    clearNewChatRequest: () => {},
    openChatWithComposerText: () => {},
    requestedComposerPrefill: null,
    clearRequestedComposerPrefill: () => {},
    openChatWithComposerDraft: () => {},
    requestedComposerDraft: null,
    clearRequestedComposerDraft: () => {},
});

export function useUI() {
    return useContext(UIContext);
}

export function UIProvider({ children }: { children: ReactNode }) {
    const [isChatVisible, setIsChatVisible] = useState(
        readPersistedChatVisible,
    );
    const [requestedChatId, setRequestedChatId] = useState<string | null>(null);
    const [requestedChatOrganizationId, setRequestedChatOrganizationId] =
        useState<string | null>(null);
    const [requestedComposerPrefill, setRequestedComposerPrefill] =
        useState<ComposerPrefillRequest | null>(null);
    const [requestedComposerDraft, setRequestedComposerDraft] =
        useState<ChatComposerDraft | null>(null);
    const [requestedChatMode, setRequestedChatMode] =
        useState<ChatPanelMode | null>(null);
    const [newChatRequested, setNewChatRequested] = useState(false);
    const wasAutoOpenedRef = useRef(false);
    const isChatVisibleRef = useRef(isChatVisible);
    const autoOpenSuppressedRef = useRef(false);
    useEffect(() => {
        writePersistedChatVisible(isChatVisible);
    }, [isChatVisible]);

    const toggleChatVisibility = useCallback(() => {
        const nextChatVisibility = !isChatVisibleRef.current;
        wasAutoOpenedRef.current = false;
        // Respect a user-driven close by suppressing auto-open until they
        // explicitly reopen the chat.
        autoOpenSuppressedRef.current = !nextChatVisibility;
        isChatVisibleRef.current = nextChatVisibility;
        setIsChatVisible(nextChatVisibility);
    }, []);

    const showChat = useCallback(() => {
        wasAutoOpenedRef.current = false;
        autoOpenSuppressedRef.current = false;
        isChatVisibleRef.current = true;
        setIsChatVisible(true);
    }, []);

    const hideChat = useCallback(() => {
        wasAutoOpenedRef.current = false;
        autoOpenSuppressedRef.current = true;
        isChatVisibleRef.current = false;
        setIsChatVisible(false);
        // Drop any draft the composer never got to consume. Closing the panel
        // ends the request; otherwise it would sit here and surface unprompted
        // the next time a composer happens to mount.
        setRequestedComposerPrefill((prev) => (prev === null ? prev : null));
        setRequestedComposerDraft((prev) => (prev === null ? prev : null));
    }, []);

    const autoOpenChat = useCallback(() => {
        if (isChatVisibleRef.current || autoOpenSuppressedRef.current) {
            return;
        }
        wasAutoOpenedRef.current = true;
        isChatVisibleRef.current = true;
        setIsChatVisible(true);
    }, []);

    const closeAutoOpenedChat = useCallback(() => {
        if (!wasAutoOpenedRef.current) {
            return;
        }
        autoOpenSuppressedRef.current = true;
        wasAutoOpenedRef.current = false;
        isChatVisibleRef.current = false;
        setIsChatVisible(false);
    }, []);

    const openChatWithId = useCallback(
        (
            chatId: string,
            options?: {
                readonly mode?: ChatPanelMode;
                readonly organizationId?: string | null;
            },
        ) => {
            if (autoOpenSuppressedRef.current) {
                return;
            }
            setRequestedChatId((prev) => (prev === chatId ? prev : chatId));
            setRequestedChatOrganizationId(options?.organizationId ?? null);
            setRequestedChatMode(options?.mode ?? null);
            isChatVisibleRef.current = true;
            setIsChatVisible(true);
        },
        [],
    );

    // Approval-toast clicks open the waiting agent chat through the module
    // registry. Registered here (not in the chat view) so the opener still
    // works while the chat panel is hidden and the view is unmounted. An
    // explicit click overrides a prior user-driven close, mirroring the
    // deep-link and sidebar-session paths.
    //
    // This site can't see the target chat's backend kind, so prefer the run
    // id: agent-run notifications target the run-native product chat, which
    // selects by run id. Fall back to the conversation id when no run id.
    useEffect(() => {
        return registerAgentConversationOpener(
            ({ agentConversationId, agentRunId, organizationId }) => {
                autoOpenSuppressedRef.current = false;
                openChatWithId(agentRunId ?? agentConversationId, {
                    organizationId,
                });
            },
        );
    }, [openChatWithId]);

    // Lets surfaces outside the chat (for example a scheduled outreach with no
    // linked conversation) open the docked panel on a fresh draft chat. The
    // chat view consumes newChatRequested and clears it.
    const openNewChat = useCallback(() => {
        setNewChatRequested(true);
        // Retire any pending open-specific-conversation request so it can't
        // race this one and land the user in an old thread.
        setRequestedChatId((prev) => (prev === null ? prev : null));
        setRequestedChatOrganizationId((prev) => (prev === null ? prev : null));
        setRequestedChatMode((prev) => (prev === null ? prev : null));
        wasAutoOpenedRef.current = false;
        autoOpenSuppressedRef.current = false;
        isChatVisibleRef.current = true;
        setIsChatVisible(true);
    }, []);

    const clearNewChatRequest = useCallback(() => {
        setNewChatRequested((prev) => (prev ? false : prev));
    }, []);

    // Lets surfaces outside the chat (for example the sidebar get-started
    // checklist or the calendar selection pill) open the docked panel with a
    // draft already in the composer. The chat view consumes
    // requestedComposerPrefill and clears it; nothing is auto-sent.
    const openChatWithComposerText = useCallback(
        (
            text: string,
            source: ComposerPrefillSource,
            onDelivered?: () => void,
            options?: { readonly newConversation?: boolean },
        ) => {
            setRequestedComposerPrefill({
                text,
                source,
                onDelivered,
                newConversation: options?.newConversation,
            });
            setRequestedComposerDraft((prev) => (prev === null ? prev : null));
            // This targets the general chat, so retire any pending request to open
            // a specific conversation — otherwise a racing approval-toast click
            // would navigate to that thread and drop this draft into it.
            setRequestedChatId((prev) => (prev === null ? prev : null));
            setRequestedChatOrganizationId((prev) =>
                prev === null ? prev : null,
            );
            setRequestedChatMode((prev) => (prev === null ? prev : null));
            wasAutoOpenedRef.current = false;
            autoOpenSuppressedRef.current = false;
            isChatVisibleRef.current = true;
            setIsChatVisible(true);
        },
        [],
    );

    const clearRequestedComposerPrefill = useCallback(() => {
        setRequestedComposerPrefill((prev) => (prev === null ? prev : null));
    }, []);

    // Activity-board handoffs carry both visible entity pills and the source
    // event IDs that the message endpoint links to the resulting agent run.
    const openChatWithComposerDraft = useCallback(
        (draft: ChatComposerDraft) => {
            setRequestedComposerDraft(draft);
            setRequestedComposerPrefill((prev) =>
                prev === null ? prev : null,
            );
            setRequestedChatId((prev) => (prev === null ? prev : null));
            setRequestedChatOrganizationId((prev) =>
                prev === null ? prev : null,
            );
            setRequestedChatMode((prev) => (prev === null ? prev : null));
            wasAutoOpenedRef.current = false;
            autoOpenSuppressedRef.current = false;
            isChatVisibleRef.current = true;
            setIsChatVisible(true);
        },
        [],
    );

    const clearRequestedComposerDraft = useCallback(() => {
        setRequestedComposerDraft((prev) => (prev === null ? prev : null));
    }, []);

    const clearRequestedChatId = useCallback(() => {
        setRequestedChatId((prev) => (prev === null ? prev : null));
        setRequestedChatOrganizationId((prev) => (prev === null ? prev : null));
    }, []);

    const clearRequestedChatMode = useCallback(() => {
        setRequestedChatMode((prev) => (prev === null ? prev : null));
    }, []);

    const clearAutoOpenSuppression = useCallback(() => {
        autoOpenSuppressedRef.current = false;
    }, []);

    const contextValue = useMemo(
        () => ({
            isChatVisible,
            toggleChatVisibility,
            showChat,
            hideChat,
            autoOpenChat,
            closeAutoOpenedChat,
            openChatWithId,
            requestedChatId,
            requestedChatOrganizationId,
            requestedChatMode,
            clearRequestedChatId,
            clearRequestedChatMode,
            clearAutoOpenSuppression,
            openNewChat,
            newChatRequested,
            clearNewChatRequest,
            openChatWithComposerText,
            requestedComposerPrefill,
            clearRequestedComposerPrefill,
            openChatWithComposerDraft,
            requestedComposerDraft,
            clearRequestedComposerDraft,
        }),
        [
            autoOpenChat,
            clearAutoOpenSuppression,
            clearNewChatRequest,
            clearRequestedChatId,
            clearRequestedComposerPrefill,
            clearRequestedComposerDraft,
            closeAutoOpenedChat,
            hideChat,
            isChatVisible,
            newChatRequested,
            openChatWithComposerText,
            openChatWithComposerDraft,
            openChatWithId,
            openNewChat,
            requestedChatId,
            requestedChatOrganizationId,
            requestedChatMode,
            requestedComposerPrefill,
            requestedComposerDraft,
            showChat,
            toggleChatVisibility,
            clearRequestedChatMode,
        ],
    );

    return (
        <UIContext.Provider value={contextValue}>{children}</UIContext.Provider>
    );
}
